import { useEffect, useId, useState } from 'react';
import { Grid3x3, Plus, X } from 'lucide-react';
import { I18n } from '@/i18n/I18n';
import { cn } from '@/frontend/lib/utils';
import { FILE_COLUMN_TYPES, InputSheet, NamesInput, NumbersInput, SheetInput, SweepInput, TableInput, TableInputColumn, findInput, isFileColumn } from '@/reports/inputs';
import { conditionDefaults, newColumn, retypeColumn, setColumnId, setColumns, sheetIds, slugId, sweepsReading, uniqueId, unitPicks, updateInput, updateSection } from '@/reports/bundles/sheetEdit';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Textarea } from '../../ui/textarea';
import { ChipsControl } from '../../reportsV2/fields';
import { FillContextProvider } from '../../reportsV2/fillContext';
import { TableControl } from '../../reportsV2/TableControl';
import { IdField, KIND_ICONS, Prop, StringListEditor, Switch, kindLabel } from './builderControls';
import { Selection } from './InputsCanvas';

const COLUMN_TYPES: Array<TableInputColumn['type']> = ['string', 'number', 'integer', 'choice', ...FILE_COLUMN_TYPES];

/** Extensions typed loosely, "csv, .set", as the list a file input takes; none for any file. */
const parseAccept = (text: string) => {
  const accept = text.split(/[\s,]+/).filter(Boolean).map((x) => (x.startsWith('.') ? x : `.${x}`));
  return accept.length ? accept : undefined;
};

/** The extensions a file input or column takes, kept as typed (a trailing comma and all) while they parse to what is stored. */
function AcceptInput({ value, onChange, ...props }: { value: string[] | undefined; onChange: (accept: string[] | undefined) => void; id?: string; 'aria-label'?: string; className?: string }) {
  const stored = (value ?? []).join(', ');
  const [draft, setDraft] = useState(stored);
  // changed from elsewhere (another input selected, an undo): show it
  useEffect(() => {
    setDraft((d) => ((parseAccept(d) ?? []).join(', ') === stored ? d : stored));
  }, [stored]);
  return <Input {...props} value={draft} placeholder=".csv, .set" onChange={(e) => { setDraft(e.target.value); onChange(parseAccept(e.target.value)); }} />;
}

/** A unit, typed or picked from its neighbours by SI prefix. */
function UnitField({ value, onChange }: { value: string; onChange: (unit: string) => void }) {
  const id = useId();
  const picks = unitPicks(value);
  return (
    <Prop label={I18n.get('rv2Unit')} htmlFor={id}>
      <Input id={id} className="w-32" value={value} placeholder="A, V, °C…" onChange={(e) => onChange(e.target.value)} />
      {picks.length > 0 && (
        <div className="flex gap-1">
          {picks.map((p) => (
            <button key={p} type="button" onClick={() => onChange(p)} className={cn('h-7 min-w-9 rounded-md border-[1px] px-2 text-xs', p === value ? 'border-sky-700 bg-sky-50 font-medium text-sky-900' : 'hover:bg-muted')}>{p}</button>
          ))}
        </div>
      )}
    </Prop>
  );
}

const TRY = { editable: true, upload: async () => null, traces: {} };

/** A table's columns, each with its identifier, and the rows a new report starts with. */
function TableProperties({ input, onChange }: { input: TableInput; onChange: (input: TableInput) => void }) {
  const set = (i: number, column: TableInputColumn) => onChange(setColumns(input, input.columns.map((c, j) => (j === i ? column : c))));
  const hasFiles = input.columns.some(isFileColumn);
  return (
    <>
      <Prop label={I18n.get('rv2Columns')}>
        <div className="flex flex-col gap-1.5">
          {input.columns.map((c, i) => (
            <div key={i} className="flex flex-col gap-1 rounded-md border-[1px] p-1.5">
              <div className="flex items-center gap-1">
                <Input aria-label={I18n.get('rv2ColumnTitle', { n: String(i + 1) })} value={c.title} onChange={(e) => set(i, { ...c, title: e.target.value })} />
                <button type="button" aria-label={I18n.get('rv2RemoveItem', { item: c.title })} className="flex size-7 shrink-0 items-center justify-center rounded text-muted-foreground hover:bg-muted" onClick={() => onChange(setColumns(input, input.columns.filter((_, j) => j !== i)))}><X className="size-3.5" /></button>
              </div>
              <div className="flex items-center gap-1">
                <select aria-label={I18n.get('rv2ColumnType')} value={c.type} onChange={(e) => set(i, retypeColumn(c, e.target.value as TableInputColumn['type']))} className="h-7 flex-1 rounded-md border-[1px] border-input bg-background px-1.5 text-xs">
                  {COLUMN_TYPES.map((t) => <option key={t} value={t}>{I18n.get(`rv2ColumnType_${t}`)}</option>)}
                </select>
                {!isFileColumn(c) && (c.type === 'number' || c.type === 'integer') && <Input aria-label={I18n.get('rv2Unit')} className="h-7 w-20 text-xs" placeholder={I18n.get('rv2Unit')} value={c.unit ?? ''} onChange={(e) => set(i, { ...c, unit: e.target.value || undefined })} />}
                {isFileColumn(c) && c.type !== 'waveform' && (
                  <label className="flex shrink-0 items-center gap-1 text-xs text-muted-foreground">
                    <input type="checkbox" checked={!!c.multiple} onChange={(e) => set(i, { ...c, multiple: e.target.checked || undefined })} /> {I18n.get('rv2Multiple')}
                  </label>
                )}
              </div>
              {!isFileColumn(c) && c.type === 'choice' && <Input aria-label={I18n.get('rv2Choices')} className="h-7 text-xs" placeholder={I18n.get('rv2ChoicesComma')} value={(c.choices ?? []).join(', ')} onChange={(e) => set(i, { ...c, choices: e.target.value.split(',').map((x) => x.trim()).filter(Boolean) })} />}
              {isFileColumn(c) && c.type === 'file' && <AcceptInput aria-label={I18n.get('rv2Accept')} className="h-7 text-xs" value={c.accept} onChange={(accept) => set(i, { ...c, accept })} />}
              {isFileColumn(c) && c.type === 'waveform' && (
                <div className="flex items-center gap-1">
                  <Input aria-label={I18n.get('rv2XAxis')} className="h-7 text-xs" placeholder="Time (s)" value={c.xLabel ?? ''} onChange={(e) => set(i, { ...c, xLabel: e.target.value || undefined })} />
                  <Input aria-label={I18n.get('rv2YAxis')} className="h-7 text-xs" placeholder="Current (A)" value={c.yLabel ?? ''} onChange={(e) => set(i, { ...c, yLabel: e.target.value || undefined })} />
                </div>
              )}
              <div className="flex items-start gap-1.5">
                <span className="mt-1 shrink-0 text-xs text-muted-foreground">{I18n.get('rv2Identifier')}</span>
                <IdField compact label={I18n.get('rv2ColumnId', { column: c.title || c.id })} value={c.id} takenMessage={I18n.get('rv2IdTakenInTable')}
                  taken={(id) => input.columns.some((x) => x.id === id)} onCommit={(id) => onChange(setColumnId(input, c.id, id))} />
              </div>
            </div>
          ))}
          <Button variant="ghost" size="sm" className="self-start" onClick={() => onChange(setColumns(input, [...input.columns, newColumn(input.columns, `Column ${input.columns.length + 1}`)]))}><Plus /> {I18n.get('rv2AddColumn')}</Button>
        </div>
      </Prop>
      <Prop label={I18n.get('rv2StartingRows')} hint={I18n.get(hasFiles ? 'rv2StartingRowsFilesHelp' : 'rv2StartingRowsHelp')}>
        <FillContextProvider value={TRY}>
          <TableControl noFiles input={input} value={input.rows ?? []} onChange={(rows) => onChange({ ...input, rows })} />
        </FillContextProvider>
      </Prop>
    </>
  );
}

/** What one kind of input asks the designer beyond its question and help. */
function KindProperties({ sheet, input, onChange, onEditSweep }: { sheet: InputSheet; input: SheetInput; onChange: (input: SheetInput) => void; onEditSweep: () => void }) {
  const acceptId = useId();
  switch (input.kind) {
    case 'number':
      return <UnitField value={input.unit ?? ''} onChange={(unit) => onChange({ ...input, unit })} />;
    case 'text':
      return <Switch label={I18n.get('rv2Multiline')} help={I18n.get('rv2MultilineHelp')} checked={!!input.multiline} onChange={(multiline) => onChange({ ...input, multiline: multiline || undefined })} />;
    case 'choice':
      return <Prop label={I18n.get('rv2Choices')}><StringListEditor values={input.choices} onChange={(choices) => onChange({ ...input, choices })} addLabel={I18n.get('rv2AddChoice')} itemLabel={(i) => I18n.get('rv2ChoiceN', { n: String(i + 1) })} /></Prop>;
    case 'names':
    case 'numbers': {
      const readers = sweepsReading(sheet, input.id);
      return (
        <>
          {input.kind === 'numbers' && <UnitField value={input.unit ?? ''} onChange={(unit) => onChange({ ...input, unit: unit || undefined } as NumbersInput)} />}
          <Prop label={I18n.get('rv2Defaults')} hint={I18n.get('rv2DefaultsHelp')}>
            <ChipsControl input={input} value={input.defaults ?? []} onChange={(defaults) => onChange({ ...input, defaults } as NamesInput | NumbersInput)} />
          </Prop>
          <Switch label={I18n.get('rv2OnlyDefaults')} help={I18n.get('rv2OnlyDefaultsHelp')} checked={!!input.onlyDefaults} onChange={(only) => onChange({ ...input, onlyDefaults: only || undefined })} />
          {readers.length > 0 && <p className="text-xs text-muted-foreground">{I18n.get('rv2ReadByRunnerFor', { sweeps: readers.map((r) => r.label).join(', ') })}</p>}
        </>
      );
    }
    case 'image':
      return <Switch label={I18n.get('rv2Multiple')} help={I18n.get('rv2MultipleImagesHelp')} checked={!!input.multiple} onChange={(multiple) => onChange({ ...input, multiple })} />;
    case 'file':
      return (
        <>
          <Switch label={I18n.get('rv2Multiple')} help={I18n.get('rv2MultipleFilesHelp')} checked={!!input.multiple} onChange={(multiple) => onChange({ ...input, multiple })} />
          <Prop label={I18n.get('rv2Accept')} htmlFor={acceptId} hint={I18n.get('rv2AcceptHelp')}>
            <AcceptInput id={acceptId} value={input.accept} onChange={(accept) => onChange({ ...input, accept })} />
          </Prop>
        </>
      );
    case 'waveform':
      return (
        <div className="grid grid-cols-2 gap-2">
          <Prop label={I18n.get('rv2XAxis')}><Input aria-label={I18n.get('rv2XAxis')} value={input.xLabel ?? ''} placeholder="Time (s)" onChange={(e) => onChange({ ...input, xLabel: e.target.value || undefined })} /></Prop>
          <Prop label={I18n.get('rv2YAxis')}><Input aria-label={I18n.get('rv2YAxis')} value={input.yLabel ?? ''} placeholder="Current (A)" onChange={(e) => onChange({ ...input, yLabel: e.target.value || undefined })} /></Prop>
        </div>
      );
    case 'table':
      return <TableProperties input={input} onChange={onChange} />;
    case 'sweep':
      return <SweepSummary sheet={sheet} sweep={input} onEdit={onEditSweep} onChange={onChange} />;
    default:
      return null;
  }
}

/** A sweep as the panel sums it up: what it repeats over and where each goes; the builder edits the rest. */
function SweepSummary({ sheet, sweep, onEdit, onChange }: { sheet: InputSheet; sweep: SweepInput; onEdit: () => void; onChange: (input: SheetInput) => void }) {
  const shelfOf = (id: string) => (sweep.layout.tabs.includes(id) ? 'tabs' : sweep.layout.columns.includes(id) ? 'columns' : 'rows');
  const only = sweep.record.length === 1 && sweep.record[0].kind === 'number' ? sweep.record[0] : null;
  return (
    <>
      {only && <UnitField value={only.unit ?? ''} onChange={(unit) => onChange({ ...sweep, record: [{ ...only, unit }] })} />}
      <Prop label={I18n.get('rv2RepeatForEach')}>
        <div className="flex flex-col gap-1 rounded-lg border-[1px] px-[0.75rem] py-2">
          {sweep.conditions.map((c) => (
            <div key={c.id} className="flex items-baseline justify-between gap-2 text-sm">
              <span className="truncate">{c.label || c.id}</span>
              <span className="shrink-0 text-xs text-muted-foreground">{conditionDefaults(sheet, c).length} · {I18n.get(`rv2Shelf_${shelfOf(c.id)}`).toLowerCase()}{c.input ? ` · ${I18n.get('rv2ByRunner')}` : ''}</span>
            </div>
          ))}
          {sweep.record.length > 1 && <p className="text-xs text-muted-foreground">{I18n.get('rv2RecordsEach', { inputs: sweep.record.map((r) => r.label).join(', ') })}</p>}
          <Button variant="link" size="sm" className="h-auto self-start p-0" onClick={onEdit}><Grid3x3 /> {I18n.get('rv2EditSweep')}</Button>
        </div>
      </Prop>
    </>
  );
}

/** The panel at the right: everything about the input or section selected on the canvas. */
export function InputProperties({ sheet, selection, onChange, onSelect, onEditSweep }: {
  sheet: InputSheet; selection: Selection; onChange: (sheet: InputSheet) => void; onSelect: (selection: Selection) => void; onEditSweep: (sweepId: string) => void;
}) {
  const ids = { label: useId(), help: useId(), title: useId() };
  if (!selection) return <p className="p-[1rem] text-sm text-muted-foreground">{I18n.get('rv2SelectSomething')}</p>;

  if (selection.kind === 'section') {
    const section = sheet.sections.find((s) => s.id === selection.id);
    if (!section) return null;
    const Icon = KIND_ICONS.section;
    return (
      <div className="flex flex-col gap-[1rem] p-[1rem]">
        <h2 className="flex items-center gap-2 text-base font-semibold"><Icon className="size-4 text-muted-foreground" /> {I18n.get('rv2Tile_section')}</h2>
        <Prop label={I18n.get('rv2Title')} htmlFor={ids.title}><Input id={ids.title} value={section.title} onChange={(e) => onChange(updateSection(sheet, section.id, { title: e.target.value }))} /></Prop>
        <Prop label={I18n.get('rv2SectionHelp')} htmlFor={ids.help}><Textarea id={ids.help} value={section.help ?? ''} onChange={(e) => onChange(updateSection(sheet, section.id, { help: e.target.value || undefined }))} /></Prop>
        <IdField value={section.id} taken={(id) => sheet.sections.some((s) => s.id === id)} onCommit={(id) => { onChange(updateSection(sheet, section.id, { id })); onSelect({ kind: 'section', id }); }} />
      </div>
    );
  }

  const input = findInput(sheet, selection.id);
  if (!input) return null;
  const Icon = KIND_ICONS[input.kind];
  const set = (next: SheetInput) => onChange(updateInput(sheet, input.id, () => next));
  return (
    <div className="flex flex-col gap-[1rem] p-[1rem]">
      <h2 className="flex items-center gap-2 text-base font-semibold"><Icon className="size-4 text-muted-foreground" /> {kindLabel(input.kind)}</h2>
      <Prop label={I18n.get('rv2Question')} htmlFor={ids.label}>
        <Input id={ids.label} value={input.label} onChange={(e) => {
          const label = e.target.value;
          // a new input's id follows its question while it is first named; after that it stays put
          const naming = selection.kind === 'input' && !!selection.naming;
          const id = naming ? uniqueId(slugId(label), sheetIds(sheet, input.id)) : input.id;
          onChange(updateInput(sheet, input.id, () => ({ ...input, label, id })));
          if (id !== input.id) onSelect({ kind: 'input', id, naming: true });
        }} onBlur={() => { if (selection.kind === 'input' && selection.naming) onSelect({ kind: 'input', id: input.id }); }} />
      </Prop>
      <Prop label={I18n.get('rv2HelpForOperator')} htmlFor={ids.help}>
        <Textarea id={ids.help} value={input.help ?? ''} onChange={(e) => set({ ...input, help: e.target.value || undefined })} />
      </Prop>
      <KindProperties sheet={sheet} input={input} onChange={set} onEditSweep={() => onEditSweep(input.id)} />
      <div className="flex flex-col gap-[0.75rem] border-t-[1px] pt-[0.75rem]">
        <Switch label={I18n.get('rv2RequiredToPublish')} help={input.kind === 'sweep' ? I18n.get('rv2RequiredSweepHelp') : undefined} checked={!!input.required} onChange={(required) => set({ ...input, required: required || undefined })} />
        <Switch label={I18n.get('rv2CarryOver')} help={I18n.get('rv2CarryOverHelp')} checked={!!input.carryOver} onChange={(carryOver) => set({ ...input, carryOver: carryOver || undefined })} />
      </div>
      <details className="border-t-[1px] pt-[0.75rem]">
        <summary className="cursor-pointer text-sm text-muted-foreground">{I18n.get('rv2Advanced')}</summary>
        <div className="mt-[0.75rem]">
          <IdField value={input.id} taken={(id) => !!findInput(sheet, id)} onCommit={(id) => { onChange(updateInput(sheet, input.id, () => ({ ...input, id }))); onSelect({ kind: 'input', id }); }} />
        </div>
      </details>
    </div>
  );
}
